import * as React from 'react'
import { observer } from 'mobx-react'
import cx from 'classnames'
import Workspace from 'iam/models/Workspace'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import styles from './BundleManagerCard.sass'
import { Bundle } from 'admin/bundle/bundle'
import { IconButton } from 'components/IconButton'
import ArchivedItemsIndicator from '../ArchivedItemIndicator'
import WorkspaceBundleViewModel, {
  SelectionState,
} from 'admin/bundle/WorkspaceBundleViewModel'
import Checkbox from '../../../../components/Checkbox'
import Button from '../../../../components/Button'

interface BundleManagerCardContainerProps {
  vm: WorkspaceBundleViewModel
  workspace: Workspace
}

export default observer(function BundleManagerCardContainer({
  vm,
  workspace,
}: BundleManagerCardContainerProps) {
  if (!vm.hasBundles) {
    return null
  }

  if (!vm.isFilteredByWorkspace) {
    return (
      <BundleManagerWorkSpaceCard
        bundles={vm.bundles}
        title={workspace.name}
        vm={vm}
      />
    )
  }

  const bundles = vm.bundlesGroupedByHavingArchivedItems
  return (
    <>
      {bundles.hasArchivedItems?.length > 0 && (
        <BundleManagerWorkSpaceCard
          bundles={bundles.hasArchivedItems}
          title="Needs attention"
          vm={vm}
        />
      )}
      {bundles.allActive?.length > 0 && (
        <BundleManagerWorkSpaceCard
          bundles={bundles.allActive}
          title={workspace.name}
          vm={vm}
        />
      )}
    </>
  )
})

interface BundleManagerWorkSpaceCardProp {
  vm: WorkspaceBundleViewModel
  title: string
  bundles: Bundle[]
}

const BundleManagerWorkSpaceCard = observer(
  function BundleManagerCardContainer({
    vm,
    title,
    bundles,
  }: BundleManagerWorkSpaceCardProp) {
    return (
      <>
        <div className={styles.workspaceHeader}>
          <div>{title}</div>

          <div className={styles.actions}>
            <div className={styles.selection}>
              <Checkbox
                checked={vm.selectedState === SelectionState.AllSelected}
                onChange={vm.toggleAll}
                data-test="bundle-duplicate-select-all"
              />
              {vm.selectedState === SelectionState.AllSelected
                ? 'All Selected'
                : 'Select All'}
            </div>

            {vm.selectedState === SelectionState.SomeSelected && (
              <div className={styles.selection}>
                <span className={styles.separator}>|</span>
                <Checkbox
                  checked="indeterminate"
                  onChange={vm.clearSelectedBundle}
                />
                {vm.selectedBundlesId.size} Selected
              </div>
            )}

            {vm.showBulkActions && (
              <Dropdown
                autoDismiss
                alignContent="right"
                position="bottom-end"
                minWidth={160}
                dataTest="bulk-actions-button"
                button={
                  <Button submit className={styles.duplicateButton}>
                    Actions
                    <Icon name="dropdown" size={14} />
                  </Button>
                }
              >
                <DropdownTitle>Actions</DropdownTitle>
                <DropdownItem
                  onClick={vm.bulkApplyDiscount.bind(vm)}
                  data-test="bulk-apply-discount-action"
                >
                  Apply discount
                </DropdownItem>
                <DropdownItem
                  onClick={vm.bulkEditPrice.bind(vm)}
                  data-test="bulk-edit-price-action"
                >
                  Edit price
                </DropdownItem>
                <DropdownItem
                  onClick={vm.bulkDuplicate.bind(vm)}
                  data-test="bulk-duplicate-action"
                >
                  Duplicate
                </DropdownItem>
                <DropdownItem
                  onClick={vm.bulkArchive.bind(vm)}
                  data-test="bulk-archive-action"
                >
                  Archive
                </DropdownItem>
              </Dropdown>
            )}
          </div>
        </div>

        <div className={styles.container}>
          {bundles.map((bundle, index) => {
            return <BundleManagerCard vm={vm} bundle={bundle} key={index} />
          })}
        </div>
      </>
    )
  },
)

interface BundleManagerCardProps {
  vm: WorkspaceBundleViewModel
  bundle: Bundle
}

const BundleManagerCard = observer(function BundleManagerCard({
  vm,
  bundle,
}: BundleManagerCardProps) {
  const includedItemsAndBaseItemCount = bundle.includedItems.length
  const itemPlural = includedItemsAndBaseItemCount === 1 ? ' item' : ' items'

  return (
    <div
      className={cx(
        styles.bundleCard,
        bundle.containsArchivedItems && styles.archivedItemsContainer,
      )}
      data-test={`bundle-card-${bundle.name}`}
    >
      <div className={styles.bundleHeader}>
        <div className={styles.workspaceIcon}>
          <img src={bundle.workspace.branding.logos.icon} />
        </div>

        <div className={styles.bundleActions}>
          <Dropdown
            minWidth={120}
            dataTest={`bundle-actions-${bundle.name}`}
            alignContent="right"
            autoDismiss
            position="bottom-end"
            button={
              <IconButton className={styles.dropdownIconButton}>
                <Icon name="more" size={24} rotate={-90} />
              </IconButton>
            }
          >
            <DropdownTitle>Actions</DropdownTitle>
            {bundle.archived && (
              <DropdownItem
                onClick={() => vm.unarchiveBundleViewModel.open(bundle)}
                data-test={`bundle-actions-unarchive-${bundle.name}`}
              >
                <div className={styles.rowActionBundle}>
                  <span>Unarchive</span>
                </div>
              </DropdownItem>
            )}
            {!bundle.archived && (
              <>
                <DropdownItem
                  onClick={() => vm.archiveBundleViewModel.open(bundle)}
                  data-test={`bundle-actions-archive-${bundle.name}`}
                >
                  <div className={styles.rowActionBundle}>
                    <span>Archive</span>
                  </div>
                </DropdownItem>
                <DropdownItem
                  onClick={() => vm.duplicateBundleViewModel.open(bundle)}
                  data-test={`bundle-actions-duplicate-${bundle.name}`}
                >
                  <div className={styles.rowActionBundle}>
                    <span>Duplicate</span>
                  </div>
                </DropdownItem>
                <DropdownItem
                  onClick={() => vm.editBundleViewModel.open(bundle)}
                  data-test={`bundle-actions-edit-${bundle.name}`}
                >
                  <div className={styles.rowActionBundle}>
                    <span>Edit</span>
                  </div>
                </DropdownItem>
              </>
            )}
          </Dropdown>

          <Checkbox
            checked={vm.isSelected(bundle.Id)}
            onChange={(checked) => vm.toggleBundle(checked, bundle.Id)}
            data-test={`select-bundle-${bundle.Id}`}
          />
        </div>
      </div>

      <div className={styles.bundleName}>{bundle.name}</div>

      <div className={styles.bundleInfo}>
        <div className={styles.bundlePricing}>${bundle.price.toFixed(2)}</div>

        <div className={styles.separator}>•</div>

        <div className={styles.bundleIncludedItems}>
          {includedItemsAndBaseItemCount + itemPlural}
        </div>
      </div>

      <div className={styles.bundleDescription}>{bundle.description}</div>

      <div className={styles.infoSeparator} />
      {!bundle.containsArchivedItems ? (
        <div className={styles.bundleLastUpdatedInfo}>
          {bundle.lastEditedBy
            ? 'Last updated by ' + bundle.lastEditedBy.displayName
            : 'Created by ' + bundle.createdBy.displayName}
        </div>
      ) : (
        <ArchivedItemsIndicator count={bundle.archivedItemsCount} />
      )}
    </div>
  )
})
